import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  MapPin,
  Sparkles,
  Users,
  Minus,
  Plus,
  X,
  AlertCircle,
  ShieldCheck,
  Star,
} from "lucide-react";
import DOMPurify from "dompurify";
import Navber from "../Components/Navber";
import api from "../Api";
import { showToast } from "./PopupMessage.jsx";
import "./ActivityDetails.css";

const ActivityDetails = () => {
  const navigate = useNavigate();
  const { slugId } = useParams();
  const parts = slugId.split("-");

  const random = parseInt(parts.pop(), 10);
  const encodedId = parseInt(parts.pop(), 10);
  const id = encodedId - random;

  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);

  // Booking states
  const [openBooking, setOpenBooking] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [seats, setSeats] = useState(1);
  const [totalAmount, setTotalAmount] = useState(0);
  const [paying, setPaying] = useState(false);

  const accessToken = localStorage.getItem("accessToken");
  const [franchiseDetails, setFranchiseDetails] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);

  /* ---------- Auto slide ---------- */
  useEffect(() => {
    if (!activity?.images?.length) return undefined;

    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % activity.images.length);
    }, 3000);

    return () => clearInterval(timer);
  }, [activity?.images?.length]);

  /* ---------- Profile ---------- */
  useEffect(() => {
    const fetchHandleProfile = async () => {
      try {
        if (!accessToken) {
          navigate("/signup");
          return;
        }
        const response = await api.get("/profile", {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setFranchiseDetails(response?.data?.customer?.franchise_details);
        setCustomerDetails(response?.data?.customer);
      } catch (error) {
        console.error("Profile fetch error:", error);
      }
    };
    fetchHandleProfile();
  }, [accessToken, navigate]);

  /* ---------- Fetch activity ---------- */
  useEffect(() => {
    const fetchActivityDetails = async () => {
      try {
        const res = await api.get(`/activities/${id}`);
        setActivity(res.data.data);
      } catch (err) {
        console.error("Error fetching activity:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchActivityDetails();
  }, [id]);

  /* ---------- Lock body scroll when modal open ---------- */
  useEffect(() => {
    document.body.style.overflow = openBooking ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [openBooking]);

  /* ---------- Slide controls ---------- */
  const prevSlide = () => {
    setIndex((prev) => (prev === 0 ? activity?.images.length - 1 : prev - 1));
  };
  const nextSlide = () => {
    setIndex((prev) => (prev + 1) % activity?.images.length);
  };

  /* ---------- Booking handlers ---------- */
  const handleOpenBooking = (destination) => {
    setSelectedDestination(destination);
    setSeats(1);
    setTotalAmount(Number(destination.price) || 0);
    setOpenBooking(true);
  };

  const handleCloseBooking = () => {
    if (paying) return;
    setOpenBooking(false);
  };

  /* ---------- FIXED: seat change now updates BOTH seats and total ---------- */
  const handleSeatChange = (type) => {
    if (!selectedDestination) return;
    const unitPrice = Number(selectedDestination.price) || 0;

    if (type === "increase") {
      const newSeats = seats + 1;
      setSeats(newSeats);
      setTotalAmount(newSeats * unitPrice);
    } else if (type === "decrease" && seats > 1) {
      const newSeats = seats - 1;
      setSeats(newSeats);                          // ← was missing before
      setTotalAmount(newSeats * unitPrice);
    }
  };

  /* ---------- Payment ---------- */
  const handlePayment = async () => {
    if (!selectedDestination) return;

    try {
      setPaying(true);
      const payload = {
        destination_id: selectedDestination.id,
        seats,
        total_amount: totalAmount,
        currency: "INR",
      };

      const order = await api.post(`/activities/${activity?.id}/book`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const options = {
        key: order?.data?.data?.razorpay_key,
        amount: order?.data?.data?.amount,
        currency: order?.data?.data?.currency,
        name: "Jayraj Group Activity",
        description: order?.data?.data?.name,
        order_id: order?.data?.data?.razorpay_order_id,
        handler: async (response) => {
          const verifyPayload = {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            franchise_id: franchiseDetails?.id || null,
          };
          try {
            const verifyRes = await api.post(
              `/activities/bookings/${order?.data?.data?.booking_id}/verify`,
              verifyPayload,
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${accessToken}`,
                },
              }
            );
            if (verifyRes?.status === 200) {
              showToast("Payment Successfully Done!", "success");
              navigate("/profile/activity-info");
            } else {
              showToast("Payment verification failed", "error");
            }
          } catch (err) {
            console.error("Verification error:", err);
            showToast("Error verifying payment", "error");
          } finally {
            setPaying(false);
          }
        },
        prefill: {
          name: customerDetails?.name || "Guest",
          email: customerDetails?.email || "guest@example.com",
          contact: customerDetails?.phone || "9999999999",
        },
        theme: { color: "#b8893a" },
        modal: { ondismiss: () => setPaying(false) },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      setOpenBooking(false);
    } catch (err) {
      console.error("Payment initiation error:", err);
      showToast("Payment initialization failed", "error");
      setPaying(false);
    }
  };

  /* ---------- Loading ---------- */
  if (loading) {
    return (
      <div className="actd-page">
        <div className="actd-navbar-slot">
          <Navber />
        </div>
        <div className="actd-shell">
          <div className="actd-loading">
            <div className="actd-skeleton actd-skeleton-media" />
            <div className="actd-skeleton-body">
              <div className="actd-skeleton-line actd-skeleton-title" />
              <div className="actd-skeleton-line" />
              <div className="actd-skeleton-line actd-skeleton-short" />
              <div className="actd-skeleton-line actd-skeleton-block" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="actd-page">
        <div className="actd-navbar-slot">
          <Navber />
        </div>
        <div className="actd-shell">
          <div className="actd-empty">
            <span className="actd-empty-icon">
              <AlertCircle />
            </span>
            <h3 className="actd-empty-title">Activity Not Found</h3>
            <p className="actd-empty-text">
              The activity you are looking for doesn't exist or has been removed.
            </p>
            <button
              type="button"
              className="actd-btn-gold"
              onClick={() => navigate(-1)}
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const sanitizedDescription = DOMPurify.sanitize(activity.description);
  const images = activity?.images || [];
  const destinations = activity?.destinations || [];

  return (
    <div className="actd-page">
      <div className="actd-navbar-slot">
        <Navber />
      </div>

      <div className="actd-shell">
        {/* ---------- Breadcrumb ---------- */}
        <nav className="actd-breadcrumb" aria-label="Breadcrumb">
          <a href="/">Home</a>
          <span aria-hidden="true">/</span>
          <a href="/activities">Activities</a>
          <span aria-hidden="true">/</span>
          <span className="actd-breadcrumb-current">{activity.name}</span>
        </nav>

        {/* ---------- Hero + details grid ---------- */}
        <div className="actd-grid">
          {/* ---------- Gallery ---------- */}
          <motion.div
            className="actd-gallery"
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          >
            <div className="actd-slides">
              <div
                className="actd-slides-track"
                style={{ transform: `translateX(-${index * 100}%)` }}
              >
                {images.map((img, i) => (
                  <div key={i} className="actd-slide">
                    <img
                      src={img?.url}
                      alt={`${activity.name} — slide ${i + 1}`}
                      loading={i === 0 ? "eager" : "lazy"}
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/800x600?text=No+Image";
                      }}
                    />
                  </div>
                ))}
              </div>

              <span className="actd-slide-veil" aria-hidden="true" />

              <span className="actd-gallery-chip">
                <Sparkles size={11} aria-hidden="true" />
                Experience
              </span>

              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    className="actd-nav actd-nav-prev"
                    onClick={prevSlide}
                    aria-label="Previous image"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    type="button"
                    className="actd-nav actd-nav-next"
                    onClick={nextSlide}
                    aria-label="Next image"
                  >
                    <ChevronRight size={20} />
                  </button>

                  <div className="actd-dots" role="tablist">
                    {images.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        role="tab"
                        aria-label={`Go to slide ${i + 1}`}
                        aria-selected={i === index}
                        className={`actd-dot ${i === index ? "is-active" : ""}`}
                        onClick={() => setIndex(i)}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </motion.div>

          {/* ---------- Details + Booking ---------- */}
          <motion.div
            className="actd-details"
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
          >
            <span className="actd-eyebrow">
              <Star size={11} fill="currentColor" aria-hidden="true" />
              Featured Activity
            </span>

            <h1 className="actd-title">{activity.name}</h1>

            {/* Full description — flows naturally, no truncation */}
            <div
              className="actd-description"
              dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
            />

            {/* ---------- Booking section (right after description) ---------- */}
            <section className="actd-booking">
              <header className="actd-booking-head">
                <span className="actd-eyebrow">
                  <MapPin size={11} aria-hidden="true" />
                  Choose Your Location
                </span>
                <h2 className="actd-booking-title">Available Destinations</h2>
                <p className="actd-booking-sub">
                  Pick where you'd like to experience this activity
                </p>
              </header>

              {destinations.length === 0 ? (
                <div className="actd-empty actd-empty-inline">
                  <span className="actd-empty-icon">
                    <AlertCircle />
                  </span>
                  <p className="actd-empty-text">
                    No destinations available at the moment.
                  </p>
                </div>
              ) : (
                <ul className="actd-booking-list">
                  {destinations.map((d, i) => (
                    <motion.li
                      key={d.id}
                      className="actd-booking-row"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.35,
                        delay: Math.min(i, 6) * 0.05,
                      }}
                    >
                      <div className="actd-booking-row-info">
                        <span className="actd-booking-row-icon">
                          <MapPin size={13} aria-hidden="true" />
                        </span>
                        <div className="actd-booking-row-text">
                          <span className="actd-booking-row-name">
                            {d.destination_name || d.name}
                          </span>
                          <span className="actd-booking-row-price">
                            ₹{Number(d.price).toLocaleString()}
                            <span className="actd-booking-row-unit">
                              {" "}
                              / person
                            </span>
                          </span>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="actd-booking-row-cta"
                        onClick={() => handleOpenBooking(d)}
                      >
                        Book
                        <ChevronRight size={13} aria-hidden="true" />
                      </button>
                    </motion.li>
                  ))}
                </ul>
              )}
            </section>

            {/* ---------- Trust row ---------- */}
            <div className="actd-trust">
              <span className="actd-trust-item">
                <ShieldCheck size={13} aria-hidden="true" />
                Secure Booking
              </span>
              <span className="actd-trust-item">
                <Users size={13} aria-hidden="true" />
                Small Groups
              </span>
              <span className="actd-trust-item">
                <Sparkles size={13} aria-hidden="true" />
                Expert Guided
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ============================================================
          Booking Modal
         ============================================================ */}
      {openBooking && (
        <div
          className="actd-modal-overlay"
          onClick={handleCloseBooking}
          role="presentation"
        >
          <motion.div
            className="actd-modal"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
          >
            <button
              type="button"
              className="actd-modal-close"
              onClick={handleCloseBooking}
              aria-label="Close booking"
              disabled={paying}
            >
              <X size={18} />
            </button>

            <span className="actd-modal-eyebrow">
              <Sparkles size={11} aria-hidden="true" />
              Reserve Your Spot
            </span>

            <h3 className="actd-modal-title">Book Your Seats</h3>

            {/* Destination */}
            <div className="actd-modal-field">
              <label className="actd-modal-label">Destination</label>
              <div className="actd-modal-value">
                <MapPin size={14} aria-hidden="true" />
                {selectedDestination?.destination_name ||
                  selectedDestination?.name}
              </div>
            </div>

            {/* Seats */}
            <div className="actd-modal-field">
              <label className="actd-modal-label">Number of Seats</label>
              <div className="actd-seat-row">
                <button
                  type="button"
                  className="actd-seat-btn"
                  onClick={() => handleSeatChange("decrease")}
                  disabled={seats <= 1}
                  aria-label="Decrease seats"
                >
                  <Minus size={16} />
                </button>
                <span className="actd-seat-count">{seats}</span>
                <button
                  type="button"
                  className="actd-seat-btn"
                  onClick={() => handleSeatChange("increase")}
                  aria-label="Increase seats"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            {/* Price breakdown */}
            <div className="actd-modal-price">
              <div className="actd-modal-price-row">
                <span>
                  ₹{Number(selectedDestination?.price || 0).toLocaleString()} × {seats}
                </span>
                <span>₹{totalAmount.toLocaleString()}</span>
              </div>
              <div className="actd-modal-price-row actd-modal-price-total">
                <span>Total</span>
                <strong>₹{totalAmount.toLocaleString()}</strong>
              </div>
            </div>

            {/* Actions */}
            <div className="actd-modal-actions">
              <button
                type="button"
                className="actd-btn-ghost"
                onClick={handleCloseBooking}
                disabled={paying}
              >
                Cancel
              </button>
              <button
                type="button"
                className="actd-btn-gold"
                onClick={handlePayment}
                disabled={paying}
              >
                {paying ? (
                  <>
                    <span className="actd-spinner" aria-hidden="true" />
                    Processing…
                  </>
                ) : (
                  "Book Now"
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ActivityDetails;